<template>
  <div style="width: 100%;">
    <routine-list
      v-if="message.content.isRoutine"
      class="products-message message"
      :message="message"
      @product-clicked="handleProductClickedEvent"
      @product-added-to-cart="handleAddedToCartEvent"
      @product-routine-added-to-cart="handleRoutineAddedToCartEvent"
      @attribute-matching-list-expanded="handleAttributeMatchingListExpandedEvent"
      @product-info-clicked="handleProductInfoClickedEvent"
    />
    <products-list
      v-else
      class="products-message message"
      :message="message"
      @product-clicked="handleProductClickedEvent"
      @product-added-to-cart="handleAddedToCartEvent"
      @attribute-matching-list-expanded="handleAttributeMatchingListExpandedEvent"
      @product-info-clicked="handleProductInfoClickedEvent"
    />
    <div v-if="isShowingMoreProducts" class="more-products-panel">
      <button type="button" class="quick-reply-button" @click="handleMoreProductsClicked">
        {{ moreProductsButtonTitle }}
      </button>
    </div>
  </div>
</template>

<script>
// @flow

import { mapGetters, mapState } from 'vuex'

import { type ProductItem } from '@chatchamp/chatchamp-types'

import { productIds } from '../../../../common/models'
import { CHATBOT_EVENTS, dispatchChatchampEvent } from '../../../../main/eventTracking'

import { postMessageMixin } from '../../../mixins'
import { sendMoreProductsMessage } from '../../../utils/api'

import ProductsList from './ProductsList/ProductsList.vue'
import RoutineList from './RoutineList/RoutineList.vue'

type AddedToCartEvent = {
  product: ProductItem,
  productId: string,
  chatbotId: number
}

type ProductClickedEvent = {
  productId: number,
  productIdentifier: string,
  chatbotId: number
}

type RoutineAddedToCartEvent = {
  products: Array<ProductItem>,
  productIds: string,
  chatbotId: number
}

type AttributeMatchingListExpandedEvent = {
  productId: number,
  chatbotId: number
}

type ProductInfoClickedEvent = {
  productId: number,
  chatbotId: number
}

export default {
  components: {
    ProductsList,
    RoutineList
  },
  mixins: [postMessageMixin],
  props: {
    message: {
      type: Object,
      default: () => {
      }
    }
  },
  data: () => {
    return {
      moreProductsButtonClicked: false
    }
  },
  computed: {
    ...mapGetters(['latestProductsMessage', 'moreProductsClicked']),
    ...mapState(['customerId', 'subscriberId', 'parentUrl']),
    isShowingMoreProducts (): boolean {
      return (
        !this.moreProductsButtonClicked &&
        this.message.content.moreProductsAvailable > 0 &&
        this.latestProductsMessage.id === this.message.id
      )
    },
    moreProductsButtonTitle (): string {
      return this.message.content.moreProductsButtonTitle || this.$t('product.loadMore')
    }
  },
  mounted () {
    if (this.message.lastMessage) {
      if (this.moreProductsClicked) {
        dispatchChatchampEvent(CHATBOT_EVENTS.MoreProductsLoaded, {
          customerId: this.customerId,
          subscriberId: this.subscriberId,
          chatbotId: this.message.content.chatbotId,
          productIds: productIds(this.message.content.elements),
          products: this.message.content.elements,
          parentUrl: this.parentUrl
        })
        this.$store.commit('setMoreProductsClicked', false)
      } else {
        dispatchChatchampEvent(CHATBOT_EVENTS.Completed, {
          customerId: this.customerId,
          subscriberId: this.subscriberId,
          chatbotId: this.message.content.chatbotId,
          productIds: productIds(this.message.content.elements),
          products: this.message.content.elements,
          parentUrl: this.parentUrl
        })
      }
    }
  },
  methods: {
    async handleMoreProductsClicked (): void {
      this.$store.commit('setMoreProductsClicked', true)
      this.moreProductsButtonClicked = true
      await sendMoreProductsMessage({
        text: this.moreProductsButtonTitle,
        messageId: this.message.content.messageId,
        alreadySeenProductIds: this.message.content.alreadySeenProductIds
      })
      await this.$store.dispatch('addTextMessageToList', this.moreProductsButtonTitle)
    },
    handleAddedToCartEvent (eventData: AddedToCartEvent): void {
      this.$store.commit('setProductAddingToCart', eventData.productId)
      this.notifyProductsAddedToCart([eventData.product], [eventData.productId], eventData.chatbotId)
      dispatchChatchampEvent(CHATBOT_EVENTS.AddedToCart, {
        customerId: this.customerId,
        subscriberId: this.subscriberId,
        chatbotId: eventData.chatbotId,
        product: eventData.product,
        productId: eventData.productId,
        parentUrl: this.parentUrl
      })
    },
    handleProductClickedEvent (eventData: ProductClickedEvent): void {
      const product = this.message.content.elements.find(element => element.productId === eventData.productId)
      this.notifyProductClicked(eventData.productId, eventData.chatbotId)
      dispatchChatchampEvent(CHATBOT_EVENTS.RecommendedProductClicked, {
        customerId: this.customerId,
        subscriberId: this.subscriberId,
        chatbotId: eventData.chatbotId,
        productId: eventData.productId,
        productIdentifier: eventData.productIdentifier,
        parentUrl: this.parentUrl,
        utility: product.utility
      })
    },
    handleRoutineAddedToCartEvent (eventData: RoutineAddedToCartEvent): void {
      this.$store.commit('setRoutineAddingToCart', true)
      this.notifyProductsAddedToCart(eventData.products, eventData.productIds, eventData.chatbotId)
      dispatchChatchampEvent(CHATBOT_EVENTS.RoutineAddedToCart, {
        customerId: this.customerId,
        subscriberId: this.subscriberId,
        chatbotId: eventData.chatbotId,
        products: eventData.products,
        productIds: eventData.productIds.join(','),
        parentUrl: this.parentUrl
      })
    },
    handleAttributeMatchingListExpandedEvent (eventData: AttributeMatchingListExpandedEvent): void {
      dispatchChatchampEvent(CHATBOT_EVENTS.AttributeMatchingListExpanded, {
        customerId: this.customerId,
        subscriberId: this.subscriberId,
        chatbotId: eventData.chatbotId,
        productId: eventData.productId,
        parentUrl: this.parentUrl
      })
    },
    handleProductInfoClickedEvent (eventData: ProductInfoClickedEvent): void {
      dispatchChatchampEvent(CHATBOT_EVENTS.RecommendedProductInfoClicked, {
        customerId: this.customerId,
        subscriberId: this.subscriberId,
        chatbotId: eventData.chatbotId,
        productId: eventData.productId,
        parentUrl: this.parentUrl
      })
    }
  }
}
</script>

<style src="./products-message.scss" />
<style src="../TextMessage/text-message.scss" />
